/**
 * Jouer un son court, hors du live : la mélodie du test des haut-parleurs, et les petits sons de
 * Sam avant d'entrer (son arrivée, une réponse touchée, « C'est parti »).
 *
 * La branche « hors live » de l'ancien `PlaySound` (`common/sound/PlaySound.js`), seule : avant
 * l'entrée, aucune salle ne tient la session audio, on peut donc la régler (lecture en mode
 * silencieux, les autres applis baissent le son) et rendre le lecteur après coup. Le moteur n'importe
 * pas `PlaySoundM` ; l'écran appelle ceci, et le test du son observe le lecteur rendu
 * (`playbackStarted`).
 *
 * Une lecture refusée (lecture automatique bloquée par le navigateur, source vide, lecture
 * interrompue) est attendue : c'est justement ce que le test du son dit à l'élève. Elle remonte en
 * rejet, et l'appelant décide d'en faire un état ; elle ne casse jamais l'écran.
 */
import { createAudioPlayer, setAudioModeAsync, type AudioPlayer, type AudioSource } from "expo-audio"

/** Le lecteur est rendu une fois le son fini : la mélodie la plus longue fait moins d'une seconde. */
const RELEASE_AFTER_MS = 6000

export async function playSoundOnce(source: AudioSource): Promise<AudioPlayer> {
    await setAudioModeAsync({
        allowsRecording: true,
        interruptionMode: "duckOthers",
        playsInSilentMode: true,
        shouldPlayInBackground: true,
    })
    const player = createAudioPlayer(source)
    player.play()
    setTimeout(() => {
        try {
            player.remove()
        } catch (error) {
            // Déjà rendu : le lecteur n'existe plus, il n'y a rien à rendre.
            void error
        }
    }, RELEASE_AFTER_MS)
    return player
}

/** Les refus de lecture attendus (lecture automatique bloquée, source vide, lecture interrompue). */
const EXPECTED_PLAYBACK_ERRORS = new Set(["NotAllowedError", "NotSupportedError", "AbortError"])

export function isExpectedPlaybackError(error: unknown): boolean {
    return error instanceof Error && EXPECTED_PLAYBACK_ERRORS.has(error.name)
}

/**
 * Un petit son d'interface (Sam arrive, une réponse touchée) : un refus attendu ne dit rien, tout
 * autre échec passe par `onUnexpected` (la télémétrie de l'appelant).
 */
export function playUiSound(source: AudioSource, onUnexpected: (error: unknown) => void): void {
    playSoundOnce(source).catch((error: unknown) => {
        if (!isExpectedPlaybackError(error)) onUnexpected(error)
    })
}
